import { take, catchError, map, switchMap, filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  DefaultReceivingPersonsLoadAction,
  DefaultReceivingPersonsLoadCompleteAction,
  DefaultReceivingPersonsLoadFailAction,
  LOAD,
  LOAD_COMPLETE,
  LOAD_DEFAULT_CONTACT,
  LOAD_DEFAULT_CONTACT_COMPLETE,
  ReceivingPersonsLoadAction,
  ReceivingPersonsLoadCompleteAction,
  ReceivingPersonsLoadFailAction,
  ReceivingPersonsSetAction,
  RemoveSelectedReceivingPerson,
} from '@ordersnproducts/modules/shared/modules/receiving-person/redux/actions/receiving-person.actions';
import { IEffect } from '../../../../../../shared/modules/dynamic-effects/effect.interface';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { IContactResponse } from '@cemex-core/cmx-api/models/contact/contact-response.interface';
import { CONTACT_TYPE, ContactService } from '@cemex-core/cmx-api';
import { JOBSITE_SET, JobsiteSetAction } from '../../../actions/jobsite-actions/jobsite.actions';
import { RESET_BUILDER } from '../../../../../actions/order-builder.actions';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { EntityType } from '@ordersnproducts/modules/shared/models/entity-type.model';
import { ContactTypeCodeEnum } from '@ordersnproducts/modules/order-builder/modules/location/models/contact-type-code.enum';
import { getJobsiteState, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { getReceivingPersonState } from '@ordersnproducts/modules/shared/modules/receiving-person/redux/selectors/index';
import { Features } from '@ordersnproducts/features';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';

@Injectable()
export class ReceivingPersonsCustomerLoadEffects implements IEffect {
  LoadReceivingPersonsAfterJobsiteSet$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JOBSITE_SET),
      filter(() => this.isEffectAvailable()),
      map((payload: JobsiteSetAction) => {
        return new ReceivingPersonsLoadAction({
          contactType: [CONTACT_TYPE.JOBSITE, CONTACT_TYPE.CUSTOMER],
          jobsiteId: payload?.payload?.shipmentLocationId,
        });
      }),
    ),
  );

  LoadReceivingPersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD),
      filter(() => this.isEffectAvailable()),
      switchMap((action: ReceivingPersonsLoadAction) => {
        return this.store
          .select(getLegalEntity)
          .pipe(take(1))
          .pipe(
            map((customer: ILegalEntity) => customer),
            switchMap((customer: ILegalEntity) => {
              const contactTypeCodes = [ContactTypeCodeEnum.JOBSITE, ContactTypeCodeEnum.JOBSITE_REQUEST];
              if (action.payload.contactType && action.payload.contactType.includes(CONTACT_TYPE.CUSTOMER)) {
                contactTypeCodes.push(ContactTypeCodeEnum.CUSTOMER);
              }

              return this.contactService
                .getJobsiteContactsV4({
                  legalEntityId: `${customer.legalEntityId}.1`,
                  shipmentLocationId: `${action.payload.jobsiteId}.2`,
                  contactTypeCode: contactTypeCodes.join(','),
                })
                .pipe(
                  map((response: IContactResponse) => {
                    return new ReceivingPersonsLoadCompleteAction(response.contacts);
                  }),
                  catchError((error) => of(new ReceivingPersonsLoadFailAction(error))),
                );
            }),
          );
      }),
    ),
  );

  ReceivingPersonReset$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RESET_BUILDER),
      map(() => new RemoveSelectedReceivingPerson()),
    ),
  );

  LoadDefaultReceivingPersonsAfterJobsiteSet$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_COMPLETE),
      filter(
        () =>
          !!this.featureToggleService.isFeatureEnabled(Features.LoadDefaultReceivingPerson) && this.isEffectAvailable(),
      ),
      switchMap(() => {
        return this.store.select(getJobsiteState).pipe(
          take(1),
          filter((state) => !!state.selected && !!state.selected.shipmentLocationCode),
          map((state) => {
            return new DefaultReceivingPersonsLoadAction({
              jobsiteCode: state.selected.shipmentLocationCode,
            });
          }),
        );
      }),
    ),
  );

  LoadDefaultReceivingPersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_DEFAULT_CONTACT),
      filter(() => this.isEffectAvailable()),
      switchMap((action: DefaultReceivingPersonsLoadAction) => {
        return this.store.select(getReceivingPersonState).pipe(
          take(1),
          filter((state) => !state.selected || !state.selected.name),
          map(() => action),
        );
      }),
      switchMap((action: DefaultReceivingPersonsLoadAction) => {
        return this.contactService
          .getContactsByJobsite({
            jobsiteCode: action.payload.jobsiteCode,
            entityType: EntityType.ORDER_REQUEST,
          })
          .pipe(
            map((response: any) => {
              return new DefaultReceivingPersonsLoadCompleteAction(response.contacts);
            }),
            catchError((error) => of(new DefaultReceivingPersonsLoadFailAction(error))),
          );
      }),
    ),
  );

  SetDefaultReceivingPersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_DEFAULT_CONTACT_COMPLETE),
      filter((action: DefaultReceivingPersonsLoadCompleteAction) => !!action.payload[0] && this.isEffectAvailable()),
      map((action: DefaultReceivingPersonsLoadCompleteAction) => {
        const apiContact = action.payload[0];
        const defaultContact = {
          contactId: apiContact.contactId,
          contactCode: apiContact.contactCode,
          name: apiContact.contactName,
          phone: apiContact.contactPhone,
        };
        return new ReceivingPersonsSetAction(defaultContact, true);
      }),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.ReceivingPersonsCustomerLoadEffects);
  }

  constructor(
    private actions$: Actions,
    private contactService: ContactService,
    private store: Store<OrderBuilderState>,
    private featureToggleService: FeatureToggleService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
